/**
 * Conversational transcript for a run. Renders text content as plain text
 * with preserved line breaks; tool_use blocks become anchor links to the
 * MCP table; tool_result blocks render as one-liners. Long text collapses
 * behind "Show more". See spec § "Component breakdown — RunTranscript".
 */
import { useState } from "react";
import type { RunResponse } from "@simpliwork/api-schema";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const COLLAPSE_THRESHOLD = 500;

type ContentBlock =
  | { type: "text"; text: string }
  | { type: "tool_use"; id: string; name: string; input: unknown }
  | { type: "tool_result"; tool_use_id: string; content: unknown };

type Message = { role: "user" | "assistant"; content: ContentBlock[] };

function isContentBlockArray(value: unknown): value is ContentBlock[] {
  return Array.isArray(value);
}

function CollapsibleText({ text }: { text: string }) {
  const [expanded, setExpanded] = useState(false);
  if (text.length <= COLLAPSE_THRESHOLD) {
    return <p className="whitespace-pre-wrap text-sm text-gray-900">{text}</p>;
  }
  const visible = expanded ? text : `${text.slice(0, COLLAPSE_THRESHOLD)}…`;
  return (
    <div>
      <p className="whitespace-pre-wrap text-sm text-gray-900">{visible}</p>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="mt-1 text-xs font-medium text-primary hover:underline"
      >
        {expanded ? "Show less" : "Show more"}
      </button>
    </div>
  );
}

export function RunTranscript({ run }: { run: RunResponse }) {
  const inputMessages = ((run.input as { messages?: Message[] } | null)?.messages ??
    []) as Message[];
  const outputMessage = (run.output as { message?: Message } | null)?.message ?? null;
  const allMessages: Message[] = [...inputMessages, ...(outputMessage ? [outputMessage] : [])];

  // Track tool_use index across the whole transcript so anchor hrefs match
  // run-mcp-table.tsx, which numbers tool_use blocks in output.message.content.
  // (For Phase-1 Anthropic Messages runs, tool_use blocks only ever appear in
  // the assistant output message, so this index lines up with the table.)
  let toolIndex = 0;

  return (
    <Card className="border-gray-200">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold text-gray-700">Transcript</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {allMessages.length === 0 && (
          <p className="text-sm text-muted-foreground">No transcript yet.</p>
        )}
        {allMessages.map((message, msgIdx) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: messages array order is stable per run, no other key available
          <div key={msgIdx} className="flex flex-col gap-2">
            <span
              className={`inline-flex w-fit items-center rounded-full px-2 py-0.5 text-xs font-semibold ${
                message.role === "user"
                  ? "bg-gray-100 text-gray-700"
                  : "bg-blue-50 text-blue-700"
              }`}
            >
              {message.role}
            </span>
            {isContentBlockArray(message.content) &&
              message.content.map((block, blockIdx) => {
                if (block.type === "text") {
                  // biome-ignore lint/suspicious/noArrayIndexKey: Anthropic content blocks have no stable id (text/tool_result are anonymous), index is the natural ordering key
                  return <CollapsibleText key={blockIdx} text={block.text} />;
                }
                if (block.type === "tool_use") {
                  const idx = toolIndex++;
                  return (
                    <a
                      // biome-ignore lint/suspicious/noArrayIndexKey: Anthropic content blocks have no stable id (text/tool_result are anonymous), index is the natural ordering key
                      key={blockIdx}
                      href={`#mcp-call-${idx}`}
                      className="text-sm text-primary hover:underline"
                    >
                      🔧 Called {block.name} →
                    </a>
                  );
                }
                if (block.type === "tool_result") {
                  return (
                    // biome-ignore lint/suspicious/noArrayIndexKey: Anthropic content blocks have no stable id (text/tool_result are anonymous), index is the natural ordering key
                    <p key={blockIdx} className="text-xs text-muted-foreground">
                      ✓ Tool result returned
                    </p>
                  );
                }
                return null;
              })}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
